import {
  accentColor,
  accentLightColor,
  aliceBlueColor,
  blackColor,
  blueStrongShade,
  cloudyIndigoColor,
  darkBlueShadeColor,
  fadeViolet,
  greenShadeColor,
  lightGreenShade,
  lightWhiteFadeColor,
  lightYellowFaded,
  redStrongColor,
  slightOrange,
  tagAmberBgColor,
  tagBlueBgColor,
  tagGrayBgColor,
  tagGreenBgColor,
  tagPurpleBgColor,
  tagRedBgColor,
  white,
  whiteSlightBlue,
} from '../../../config/ColorObj';

/** Converts a hex color to an rgba string. */
export const withAlpha = (hex, alpha) => {
  const normalized = hex.replace('#', '').trim();
  const value =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized;
  const int = parseInt(value, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r},${g},${b},${alpha})`;
};

export const DASHBOARD_COLORS = {
  bg: lightWhiteFadeColor,
  bg2: white,
  bg3: aliceBlueColor,
  surface: white,
  surface2: whiteSlightBlue,
  border: withAlpha(blackColor, 0.08),
  border2: withAlpha(blackColor, 0.14),
  text: darkBlueShadeColor,
  text2: cloudyIndigoColor,
  text3: lightYellowFaded,
  blue: accentColor,
  blueLight: accentLightColor,
  green: greenShadeColor,
  greenLight: tagGreenBgColor,
  amber: slightOrange,
  amberLight: tagAmberBgColor,
  red: redStrongColor,
  redLight: tagRedBgColor,
  violet: fadeViolet,
  violetLight: tagPurpleBgColor,
  teal: blueStrongShade,
  tealLight: lightGreenShade,
  grayLight: tagGrayBgColor,
  shadow: withAlpha(blackColor, 0.08),
  shadowSoft: withAlpha(blackColor, 0.05),
  trackBg: withAlpha(blackColor, 0.07),
  rowHover: withAlpha(blackColor, 0.02),
  scrollbarThumb: withAlpha(blackColor, 0.12),
  shimmerMid: withAlpha(white, 0.9),
  violetAlpha30: withAlpha(fadeViolet, 0.3),
  blueAlpha25: withAlpha(accentColor, 0.25),
  amberAlpha85: withAlpha(slightOrange, 0.85),
  redAlpha40: withAlpha(redStrongColor, 0.4),
};

export const CSS_VAR_NAMES = {
  bg: '--bg',
  bg2: '--bg2',
  bg3: '--bg3',
  surface: '--surface',
  surface2: '--surface2',
  border: '--border',
  border2: '--border2',
  text: '--text',
  text2: '--text2',
  text3: '--text3',
  blue: '--blue',
  blueLight: '--blue-l',
  green: '--green',
  greenLight: '--green-l',
  amber: '--amber',
  amberLight: '--amber-l',
  red: '--red',
  redLight: '--red-l',
  violet: '--violet',
  violetLight: '--violet-l',
  teal: '--teal',
  tealLight: '--teal-l',
};

export const buildCssVariablesBlock = () => {
  const lines = Object.entries(CSS_VAR_NAMES).map(
    ([key, varName]) => `    ${varName}: ${DASHBOARD_COLORS[key]};`,
  );
  return lines.join('\n');
};

export const FUNNEL_COLOR_MAP = {
  amber: {
    bg: DASHBOARD_COLORS.amberLight,
    border: DASHBOARD_COLORS.amber,
    text: DASHBOARD_COLORS.amber,
  },
  blue: {
    bg: DASHBOARD_COLORS.blueLight,
    border: DASHBOARD_COLORS.blue,
    text: DASHBOARD_COLORS.blue,
  },
  violet: {
    bg: DASHBOARD_COLORS.violetLight,
    border: DASHBOARD_COLORS.violet,
    text: DASHBOARD_COLORS.violet,
  },
  green: {
    bg: DASHBOARD_COLORS.greenLight,
    border: DASHBOARD_COLORS.green,
    text: DASHBOARD_COLORS.green,
  },
};

export const CHALLAN_LEGEND_COLORS = [
  DASHBOARD_COLORS.green,
  DASHBOARD_COLORS.blue,
  DASHBOARD_COLORS.amber,
  DASHBOARD_COLORS.red,
];

export const SECTION_ICON_BACKGROUNDS = {
  blue: DASHBOARD_COLORS.blueLight,
  teal: DASHBOARD_COLORS.tealLight,
  violet: DASHBOARD_COLORS.violetLight,
  green: DASHBOARD_COLORS.greenLight,
};

export const CHART_COLORS = {
  grid: withAlpha(blackColor, 0.06),
  blue: DASHBOARD_COLORS.blue,
  blueAlpha: withAlpha(DASHBOARD_COLORS.blue, 0.15),
  green: DASHBOARD_COLORS.green,
  greenAlpha: withAlpha(DASHBOARD_COLORS.green, 0.12),
  amber: DASHBOARD_COLORS.amber,
  red: DASHBOARD_COLORS.red,
  violet: DASHBOARD_COLORS.violet,
  teal: DASHBOARD_COLORS.teal,
  tooltipBg: DASHBOARD_COLORS.bg2,
  tooltipTitle: DASHBOARD_COLORS.text,
  tooltipBody: DASHBOARD_COLORS.text2,
  tooltipBorder: withAlpha(blackColor, 0.1),
  chartDefault: DASHBOARD_COLORS.text2,
  chartBorder: DASHBOARD_COLORS.bg2,
  violetAlpha30: DASHBOARD_COLORS.violetAlpha30,
  blueAlpha25: DASHBOARD_COLORS.blueAlpha25,
  amberAlpha85: DASHBOARD_COLORS.amberAlpha85,
  redAlpha40: DASHBOARD_COLORS.redAlpha40,
};

export const progressColor = (percent) =>
  percent >= 100 ? DASHBOARD_COLORS.green : DASHBOARD_COLORS.amber;
